var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { invalidate, measurementsAdapter, measurementsError, measurementsLoading, measurementsReceived, setGloballyUniqueMeasurementColors, setDataScale, } from "../redux/measurements.slice";
export const MeasurementsModuleContext = React.createContext({});
/**
 * Fetches all measurements for all given files using the provided client.
 * @param client The MeasurementDataClient used to retrieve measurement metadata.
 * @param files A list of files we want to retrieve measurements from.
 * @returns
 */
function fetchMeasurementsForFiles(client, files) {
    return __awaiter(this, void 0, void 0, function* () {
        const promises = files.map((f) => client.fetchMetadata(f.path).then((measurementsData) => {
            let measurements;
            // if a file errors, don't throw an error, but return error and continue to be able to retrieve other files
            if ("code" in measurementsData) {
                if (measurementsData.code !== 200) {
                    return [{ error: f.path }];
                }
                else {
                    measurements = measurementsData.response;
                }
            }
            else {
                measurements = measurementsData;
            }
            return measurements.map((m) => (Object.assign(Object.assign(Object.assign({}, m), f.timing), { file: f.path, id: `${f.group}:${m.index.join("/")}` })));
        }));
        const all = yield Promise.all(promises);
        return all.reduce((acc, c) => acc.concat(c), []);
    });
}
/**
 * Exposes the functionality contained in this module to its children components.
 *
 * Also takes care of performing requests for retrieving measurements metadata.
 *
 * TODO: Maybe this is not the best place for performing the requests for retrieving metadata.
 *
 * @param props
 * @returns
 */
export const MeasurementsModuleProvider = (props) => {
    const { selector, client, children } = props;
    const dispatch = useDispatch();
    const entitiesSelectors = useMemo(() => {
        return {
            measurements: measurementsAdapter.getSelectors(selector),
        };
    }, [selector]);
    const { files, loading, didInvalidate, globallyUniqueMeasurementColors, dataScale, } = useSelector((s) => {
        const state = selector(s);
        return state;
    });
    useEffect(() => {
        dispatch(invalidate);
    }, [dispatch, files]);
    useEffect(() => {
        if (files.length === 0 || !didInvalidate || loading)
            return;
        (function doFetchMeasurements() {
            return __awaiter(this, void 0, void 0, function* () {
                dispatch(measurementsLoading());
                let measurements = [];
                try {
                    measurements = yield fetchMeasurementsForFiles(client, files);
                }
                catch (err) {
                    dispatch(measurementsError(err.message || "Something went wrong retrieving data"));
                    return;
                }
                // return measurements from files without errors
                const loadedMeasurements = measurements.filter((m) => !("error" in m));
                dispatch(measurementsReceived(loadedMeasurements));
                // if one or multiple errors occurred, dispatch error message but return the corectly retrieved measurements
                const errorMeasurements = measurements.filter((m) => "error" in m);
                if (errorMeasurements.length > 0) {
                    dispatch(measurementsError(new Error("Error retrieving metadata for files: " +
                        errorMeasurements.map((m) => m.error).join(", "))));
                }
            });
        })();
    }, [files, didInvalidate, client, dispatch, loading]);
    useEffect(() => {
        dispatch(setGloballyUniqueMeasurementColors(globallyUniqueMeasurementColors));
    }, [dispatch, globallyUniqueMeasurementColors]);
    useEffect(() => {
        dispatch(setDataScale(dataScale));
    }, [dispatch, dataScale]);
    return (React.createElement(MeasurementsModuleContext.Provider, { value: { client, selector, entitiesSelectors } }, children));
};
//# sourceMappingURL=MeasurementsModuleProvider.js.map